import React, { useEffect, useRef, useState } from 'react';
import { useContent } from '../i18n/LanguageContext';
import { BarChart3 } from 'lucide-react';

function Counter({ target, suffix }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const dur = 1400; const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min((t - t0) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(eased * target));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [target]);
  return <span ref={ref}>{val}{suffix}</span>;
}

export default function Solutions() {
  const c = useContent();
  const s = c.solutions;
  return (
    <section className="py-14 md:py-20 bg-slate-50">
      <div className="lt-container">
        <div className="grid md:grid-cols-2 gap-6 reveal">
          {s.stats.map((st) => (
            <div key={st.label} className="rounded-3xl bg-white border border-slate-200 p-8 md:p-10 flex items-center gap-6">
              <div className="font-display text-5xl md:text-6xl font-bold text-gradient shrink-0">
                <Counter target={st.value} suffix={st.suffix} />
              </div>
              <p className="text-slate-600 text-lg">{st.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 marquee-mask overflow-hidden reveal">
          <div className="flex gap-3 w-max marquee-track">
            {[...s.reports, ...s.reports].map((rp, i) => (
              <span key={i} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600">
                <BarChart3 className="w-4 h-4 text-teal-600" />{rp}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-16 max-w-3xl reveal">
          <p className="text-sm font-semibold uppercase tracking-widest text-teal-600">{s.eyebrow}</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">{s.title}</h2>
          <p className="mt-4 text-lg text-slate-600">{s.subtitle}</p>
        </div>

        <div className="mt-14 space-y-16 md:space-y-24">
          {s.items.map((it, i) => (
            <div key={it.title} className={`grid lg:grid-cols-2 gap-8 lg:gap-14 items-center reveal ${i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
              <div className="relative">
                <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-teal-100 to-cyan-100 opacity-60 blur-md" />
                <img src={it.img} alt={it.title} className="relative rounded-[1.75rem] w-full h-72 md:h-80 object-cover shadow-xl border border-white" />
              </div>
              <div>
                <span className="inline-block text-xs font-bold text-teal-600 border border-teal-200 rounded-full px-3 py-1">0{i + 1}</span>
                <h3 className="mt-4 font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight">{it.title}</h3>
                <p className="mt-4 text-slate-600 text-lg leading-relaxed">{it.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
